// ════════════════════════════════════════════════════════════════════
// External contact / scheduling links.
// Centralized so the URLs stay in sync across every CTA in the app
// (assessment report, action plan task, sustainable route, upgrade,
// diagnostic CTAs, etc.). Update once here when the calendar moves.
//
// `?month=YYYY-MM` is built at access time from the current date so
// Calendly always opens on the user's current month — no more stale
// hardcoded `2025-07` after the calendar year rolls over.
// ════════════════════════════════════════════════════════════════════

const buildCalendlyUrl = (slug) => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `https://calendly.com/${slug}?month=${yyyy}-${mm}`;
};

// Plain object with getters so callers can keep using `CALENDLY.expertCall`
// (no parens) in templates and `:href` bindings — each read recomputes
// the URL with the current month.
export const CALENDLY = {
  // 30-min expert call — used by the "Talk with an expert" CTAs in the
  // assessment report, action plan task detail and sustainable route.
  get expertCall() {
    return buildCalendlyUrl('litzy-greenmyexperience/30min');
  },

  // 30-min cohort fit call — used by the /upgrade page and the diagnostic
  // funnel ("see if Cohort is the right fit").
  get cohortFitCall() {
    return buildCalendlyUrl('litzy-greenmyexperience/30min');
  },

  // Demo bookings — every "Book a demo / call" CTA on the public /demo pages
  // routes here (Cristina Calvo).
  get demoCall() {
    return buildCalendlyUrl('cris-calvo/let-s-connect-cristina-calvo');
  },
};

export const GUIDANCE_CALENDLY = {
  kickoff:   'litzy-greenmyexperience/30min', // kickoff privado (Core)
  results:   'litzy-greenmyexperience/30min', // lectura de resultados, 30 min (Baseline)
  workshop:  'litzy-greenmyexperience/30min', // taller / hito privado, 90 min (Private)
  review:    'litzy-greenmyexperience/30min', // revisión final privada, 90 min (Cohort)
  mockAudit: 'litzy-greenmyexperience/30min', // mock audit, medio día (Private)
  group:     'litzy-greenmyexperience/30min', // sesión grupal (evento grupal de Calendly)
};

/**
 * Link de Calendly con los datos del cliente precargados, para que no tenga
 * que escribir nada y GME sepa quién agenda y para qué sesión.
 * Calendly lee `name`, `email` y las respuestas `a1…a10` de la URL.
 */
export const guidanceBookingUrl = (kind, { name = '', email = '', company = '', session = '' } = {}) => {
  const slug = GUIDANCE_CALENDLY[kind] || GUIDANCE_CALENDLY.group;
  const url = new URL(buildCalendlyUrl(slug));
  if (name) url.searchParams.set('name', name);
  if (email) url.searchParams.set('email', email);
  if (company) url.searchParams.set('a1', company);
  if (session) url.searchParams.set('a2', session);
  url.searchParams.set('utm_source', 'gme-app');
  url.searchParams.set('utm_campaign', 'guidance');
  return url.toString();
};

// Same from-address the backend mailer sends transactional email from
// (GME-Api `controller/send.mail.controller.ts`). Used for the mailto: CTAs
// where there's no dedicated endpoint yet (e.g. subscription renewal).
export const SUPPORT_EMAIL = 'support@greenmyexperience.com';
